import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, Search, Filter, Eye, CheckCircle, XCircle, 
  Clock, AlertCircle, Paperclip, MessageSquare, User,
  FileSpreadsheet, FolderOpen, Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Utility function to get status badge - shared between components
const getStatusBadge = (status) => {
  const variants = {
    draft: { variant: 'secondary', icon: Clock, label: 'Draft' },
    submitted: { variant: 'default', icon: AlertCircle, label: 'Submitted' },
    reviewing: { variant: 'default', icon: Eye, label: 'Reviewing' },
    quoted: { variant: 'default', icon: FileSpreadsheet, label: 'Quoted' },
    accepted: { variant: 'default', icon: CheckCircle, label: 'Accepted' },
    declined: { variant: 'destructive', icon: XCircle, label: 'Declined' }
  };

  const config = variants[status] || variants.draft;
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className="text-xs font-mono uppercase">
      <Icon size={12} className="mr-1" />
      {config.label}
    </Badge>
  );
};

const EstimateManager = () => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    loadEstimates();
  }, []);

  const loadEstimates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/estimates/requests`);
      setEstimates(response.data);
    } catch (error) {
      toast.error('Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (estimateId, newStatus) => {
    try {
      await axios.put(`${API}/estimates/requests/${estimateId}/status?status=${newStatus}`);
      toast.success('Status updated successfully');
      loadEstimates();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewDetail = async (estimate) => {
    setSelectedEstimate(estimate);
    setShowDetail(true);
  };

  const filteredEstimates = estimates.filter(est => {
    const matchesSearch = 
      est.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      est.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      est.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || est.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: estimates.length,
    submitted: estimates.filter(e => e.status === 'submitted').length,
    reviewing: estimates.filter(e => e.status === 'reviewing').length,
    quoted: estimates.filter(e => e.status === 'quoted').length
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <FileText className="mx-auto mb-4 animate-pulse" size={48} />
          <p>Loading estimates...</p>
        </div>
      </div>
    );
  }

  if (showDetail && selectedEstimate) {
    return (
      <EstimateDetail 
        estimate={selectedEstimate} 
        onClose={() => {
          setShowDetail(false);
          setSelectedEstimate(null);
          loadEstimates();
        }}
        onStatusUpdate={handleStatusUpdate}
      />
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
          Estimate Requests
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Review & Manage Customer Estimates
        </p>
      </div>

      {/* Filters & Search */}
      <Card className="border-border mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by project, customer, or email..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex gap-2">
              <Button
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('all')}
                size="sm"
                className="font-mono"
              >
                All ({statusCounts.all})
              </Button>
              <Button
                variant={statusFilter === 'submitted' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('submitted')}
                size="sm"
                className="font-mono"
              >
                New ({statusCounts.submitted})
              </Button>
              <Button
                variant={statusFilter === 'reviewing' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('reviewing')}
                size="sm"
                className="font-mono"
              >
                Reviewing ({statusCounts.reviewing})
              </Button>
              <Button
                variant={statusFilter === 'quoted' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('quoted')}
                size="sm"
                className="font-mono"
              >
                Quoted ({statusCounts.quoted})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estimates List */}
      {filteredEstimates.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto mb-4 opacity-20" size={48} />
            <p className="text-muted-foreground">No estimate requests found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredEstimates.map((estimate) => (
            <Card 
              key={estimate.id} 
              className="border-border hover:border-primary transition-colors cursor-pointer"
              onClick={() => handleViewDetail(estimate)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{estimate.project_name}</h3>
                      {getStatusBadge(estimate.status)}
                      {estimate.status === 'submitted' && (
                        <Badge variant="destructive" className="text-xs animate-pulse">
                          NEW
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Customer</p>
                        <p className="font-medium">{estimate.customer_name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Project Type</p>
                        <p className="font-medium">{estimate.project_type}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Budget Range</p>
                        <p className="font-medium">{estimate.budget_range || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Timeline</p>
                        <p className="font-medium">{estimate.timeline || 'Flexible'}</p>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {estimate.description}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {estimate.files?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Paperclip size={14} />
                          <span>{estimate.files.length} files</span>
                        </div>
                      )}
                      {estimate.ai_chat_history?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <MessageSquare size={14} />
                          <span>{estimate.ai_chat_history.length} messages</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>
                          {new Date(estimate.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetail(estimate);
                    }}
                    variant="outline"
                    className="font-mono uppercase tracking-wider"
                  >
                    <Eye size={16} className="mr-2" />
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Estimate Detail View Component
const EstimateDetail = ({ estimate, onClose, onStatusUpdate }) => {
  const navigate = useNavigate();
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false);
  const [showAIResults, setShowAIResults] = useState(false);
  const [loadingSavedAnalysis, setLoadingSavedAnalysis] = useState(false);

  // Load saved AI analysis when component mounts
  useEffect(() => {
    const loadSavedAnalysis = async () => {
      if (estimate.latest_ai_analysis_id || estimate.ai_analyzed_at) {
        setLoadingSavedAnalysis(true);
        try {
          const response = await axios.get(`${API}/estimates/requests/${estimate.id}/ai-analysis`);
          console.log('Loaded saved AI analysis:', response.data);
          setAiAnalysis(response.data);
          // Auto-show if analysis exists
          if (response.data && response.data.analysis_data) {
            setShowAIResults(true);
          }
        } catch (error) {
          console.log('No saved analysis found');
        } finally {
          setLoadingSavedAnalysis(false);
        }
      }
    };
    loadSavedAnalysis();
  }, [estimate.id, estimate.latest_ai_analysis_id]);

  const handleAIAnalysis = async () => {
    setAnalyzingWithAI(true);
    try {
      const response = await axios.post(`${API}/estimates/requests/${estimate.id}/ai-analysis`);
      console.log('AI Response:', response.data);
      console.log('Analysis data:', response.data.analysis);
      setAiAnalysis(response.data);
      setShowAIResults(true);
      toast.success('AI analysis complete!');
    } catch (error) {
      toast.error('AI analysis failed. Please try again.');
      console.error('AI analysis error:', error);
    } finally {
      setAnalyzingWithAI(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          onClick={onClose}
          variant="outline"
          className="mb-4"
        >
          ← Back to Estimates
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-black uppercase mb-2">
              {estimate.project_name}
            </h1>
            <p className="text-muted-foreground">
              Estimate Request #{estimate.id.slice(0, 8)}
            </p>
          </div>
          {getStatusBadge(estimate.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Details */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-bold uppercase">
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                    Project Type
                  </Label>
                  <p className="font-medium mt-1">{estimate.project_type}</p>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                    Form Type
                  </Label>
                  <p className="font-medium mt-1">{estimate.form_type}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                  Description
                </Label>
                <p className="mt-1 whitespace-pre-wrap">{estimate.description}</p>
              </div>

              {estimate.dimensions && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                    Dimensions
                  </Label>
                  <p className="mt-1">{estimate.dimensions}</p>
                </div>
              )}

              {estimate.materials_requested && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                    Materials Requested
                  </Label>
                  <p className="mt-1">{estimate.materials_requested}</p>
                </div>
              )}

              {estimate.specifications && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                    Technical Specifications
                  </Label>
                  <p className="mt-1 whitespace-pre-wrap">{estimate.specifications}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Files */}
          {estimate.files?.length > 0 && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-bold uppercase">
                  <Paperclip className="inline mr-2" size={20} />
                  Attachments ({estimate.files.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {estimate.files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-sm">
                      <div className="flex items-center gap-3">
                        <FileText size={20} />
                        <div>
                          <p className="font-medium text-sm">{file.filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {file.file_type} • {(file.file_size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Chat History */}
          {estimate.ai_chat_history?.length > 0 && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-bold uppercase">
                  <MessageSquare className="inline mr-2" size={20} />
                  AI Chat History ({estimate.ai_chat_history.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {estimate.ai_chat_history.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-sm ${
                        msg.role === 'user' ? 'bg-blue-50 dark:bg-blue-950/20' : 'bg-muted'
                      }`}
                    >
                      <p className="text-xs font-mono text-muted-foreground mb-1">
                        {msg.role === 'user' ? 'Customer' : 'AI Assistant'}
                      </p>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase">
                <User className="inline mr-2" size={18} />
                Customer Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                  Name
                </Label>
                <p className="font-medium mt-1">{estimate.customer_name}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                  Email
                </Label>
                <p className="font-medium mt-1">{estimate.customer_email}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                  Budget Range
                </Label>
                <p className="font-medium mt-1">{estimate.budget_range || 'Not specified'}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                  Timeline
                </Label>
                <p className="font-medium mt-1">{estimate.timeline || 'Flexible'}</p>
              </div>
            </CardContent>
          </Card>

          {/* AI Quote Intelligence */}
          <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-blue-500/10">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase flex items-center gap-2">
                <AlertCircle className="text-primary" size={20} />
                AI Quote Intelligence
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Get AI-powered insights: requirements analysis, pricing intelligence, difficulty assessment, questions to ask, and sales strategy.
              </p>
              
              {aiAnalysis && !showAIResults && (
                <div className="mb-3 p-2 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <p className="text-xs text-green-600 flex items-center gap-2">
                    <CheckCircle size={14} />
                    Previous analysis available from {new Date(aiAnalysis.generated_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Button
                  onClick={handleAIAnalysis}
                  disabled={analyzingWithAI}
                  className="w-full bg-gradient-to-r from-primary to-blue-500 text-white hover:from-primary/90 hover:to-blue-500/90 font-mono uppercase tracking-wider"
                >
                  {analyzingWithAI ? (
                    <>
                      <Clock className="mr-2 animate-spin" size={16} />
                      Analyzing with AI...
                    </>
                  ) : (
                    <>
                      <AlertCircle className="mr-2" size={16} />
                      {aiAnalysis ? 'Re-Analyze with AI' : 'Analyze with AI'}
                    </>
                  )}
                </Button>
                
                {aiAnalysis && !showAIResults && (
                  <Button
                    onClick={() => setShowAIResults(true)}
                    variant="outline"
                    className="w-full font-mono uppercase text-xs"
                  >
                    <Eye className="mr-2" size={14} />
                    View Saved Analysis
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status Management */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase">
                Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => onStatusUpdate(estimate.id, 'reviewing')}
                disabled={estimate.status === 'reviewing'}
                variant="outline"
                className="w-full font-mono uppercase tracking-wider"
              >
                Mark as Reviewing
              </Button>
              <Button
                onClick={() => navigate(`/quote-builder?estimate_id=${estimate.id}`)}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
              >
                <FileSpreadsheet className="mr-2" size={16} />
                Create Quote
              </Button>
              <Button
                onClick={() => onStatusUpdate(estimate.id, 'declined')}
                variant="destructive"
                className="w-full font-mono uppercase tracking-wider"
              >
                <XCircle className="mr-2" size={16} />
                Decline
              </Button>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase">
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Created</p>
                <p className="font-mono">{new Date(estimate.created_at).toLocaleString()}</p>
              </div>
              {estimate.submitted_at && (
                <div>
                  <p className="text-muted-foreground text-xs">Submitted</p>
                  <p className="font-mono">{new Date(estimate.submitted_at).toLocaleString()}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground text-xs">Last Updated</p>
                <p className="font-mono">{new Date(estimate.updated_at).toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Analysis Results - Visual Dashboard */}
      {showAIResults && aiAnalysis && (aiAnalysis.analysis || aiAnalysis.analysis_data) && (() => {
        // Normalize the data structure (handle both old and new format)
        const analysisData = aiAnalysis.analysis || aiAnalysis.analysis_data;
        const analyzedBy = aiAnalysis.analyzed_by || aiAnalysis.analyzed_by_name;
        
        return (
          <div className="mt-6 space-y-4">
          {/* Header */}
          <Card className="border-primary/50 bg-gradient-to-r from-primary/10 to-blue-500/10">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-2xl font-black uppercase flex items-center gap-2">
                    <AlertCircle className="text-primary" size={24} />
                    AI Quote Intelligence Report
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    Generated {new Date(aiAnalysis.generated_at).toLocaleString()} by {analyzedBy}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAIResults(false)}
                >
                  <XCircle size={20} />
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Budget Speedometer & Difficulty Gauges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Budget Rating */}
            {analysisData.budget && (
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-bold uppercase">Budget Assessment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-4">
                    <div className="text-5xl font-black mb-2">
                      {analysisData.budget.rating || 'N/A'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Score: {analysisData.budget.rating_score || 0}/100
                    </div>
                  </div>
                  {/* Budget Bar */}
                  <div className="w-full bg-border rounded-full h-4 mb-4">
                    <div 
                      className={`h-4 rounded-full transition-all ${
                        (analysisData.budget.rating_score || 0) >= 80 ? 'bg-green-500' :
                        (analysisData.budget.rating_score || 0) >= 60 ? 'bg-blue-500' :
                        (analysisData.budget.rating_score || 0) >= 40 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${analysisData.budget.rating_score || 0}%` }}
                    />
                  </div>
                  {/* AI Reasoning */}
                  {analysisData.budget.rating_explanation && (
                    <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                      <p className="text-xs font-mono uppercase text-blue-600 mb-1">Why This Rating?</p>
                      <p className="text-sm">{analysisData.budget.rating_explanation}</p>
                    </div>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Market Range:</span>
                      <span className="font-mono">${analysisData.budget.market_rate_min?.toLocaleString()} - ${analysisData.budget.market_rate_max?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Recommended:</span>
                      <span className="font-mono font-bold text-primary">${analysisData.budget.recommended_price?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Target Margin:</span>
                      <span className="font-mono">{analysisData.budget.profit_margin}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Difficulty & Fit */}
            {analysisData.difficulty && (
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-bold uppercase">Difficulty & Fit Analysis</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Complexity */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Complexity</span>
                      <span className="text-sm font-bold">{analysisData.difficulty.complexity_level} ({analysisData.difficulty.complexity_score}%)</span>
                    </div>
                    <div className="w-full bg-border rounded-full h-3 mb-2">
                      <div 
                        className="bg-blue-500 h-3 rounded-full transition-all"
                        style={{ width: `${analysisData.difficulty.complexity_score || 0}%` }}
                      />
                    </div>
                    {analysisData.difficulty.complexity_explanation && (
                      <p className="text-xs text-muted-foreground italic">{analysisData.difficulty.complexity_explanation}</p>
                    )}
                  </div>
                  
                  {/* Fit */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">PSM Capability Fit</span>
                      <span className="text-sm font-bold">{analysisData.difficulty.fit_level} ({analysisData.difficulty.fit_score}%)</span>
                    </div>
                    <div className="w-full bg-border rounded-full h-3 mb-2">
                      <div 
                        className="bg-green-500 h-3 rounded-full transition-all"
                        style={{ width: `${analysisData.difficulty.fit_score || 0}%` }}
                      />
                    </div>
                    {analysisData.difficulty.fit_explanation && (
                      <p className="text-xs text-muted-foreground italic">{analysisData.difficulty.fit_explanation}</p>
                    )}
                  </div>

                  {/* Challenges */}
                  {analysisData.difficulty.challenges && analysisData.difficulty.challenges.length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-xs font-mono uppercase text-muted-foreground mb-2">Key Challenges:</p>
                      <ul className="space-y-1">
                        {analysisData.difficulty.challenges.map((challenge, idx) => (
                          <li key={idx} className="text-sm flex items-start gap-2">
                            <AlertCircle size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Requirements Table */}
          {analysisData.requirements && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-bold uppercase">Requirements Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-mono uppercase text-xs">Status</th>
                        <th className="text-left py-2 px-3 font-mono uppercase text-xs">Requirement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisData.requirements.stated?.map((req, idx) => (
                        <tr key={`stated-${idx}`} className="border-b">
                          <td className="py-2 px-3">
                            <CheckCircle size={16} className="text-green-500" />
                          </td>
                          <td className="py-2 px-3">{req}</td>
                        </tr>
                      ))}
                      {analysisData.requirements.missing?.map((req, idx) => (
                        <tr key={`missing-${idx}`} className="border-b bg-yellow-500/10">
                          <td className="py-2 px-3">
                            <AlertCircle size={16} className="text-yellow-500" />
                          </td>
                          <td className="py-2 px-3 font-medium">{req}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Action Items */}
                {analysisData.requirements.action_items && analysisData.requirements.action_items.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-mono uppercase text-muted-foreground mb-2">Action Items:</p>
                    <ul className="space-y-2">
                      {analysisData.requirements.action_items.map((action, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <input type="checkbox" className="mt-1" />
                          <span className="text-sm">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Questions Checklist */}
          {analysisData.questions && analysisData.questions.length > 0 && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-bold uppercase">Questions for Customer</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {analysisData.questions.map((q, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 border border-border rounded-sm hover:bg-accent/50">
                      <input type="checkbox" className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={q.priority === 'High' ? 'destructive' : 'secondary'} className="text-xs">
                            {q.priority}
                          </Badge>
                        </div>
                        <p className="text-sm">{q.question}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Sales Strategy Cards */}
          {analysisData.sales_strategy && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-bold uppercase">Sales Strategy & Value-Adds</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Value Adds */}
                  {analysisData.sales_strategy.value_adds && analysisData.sales_strategy.value_adds.length > 0 && (
                    <div>
                      <h4 className="font-mono uppercase text-xs text-muted-foreground mb-2">Recommended Value-Adds</h4>
                      <div className="space-y-2">
                        {analysisData.sales_strategy.value_adds.map((item, idx) => (
                          <div key={idx} className="p-3 border border-green-500/30 bg-green-500/10 rounded-sm">
                            <div className="flex items-start gap-2">
                              <CheckCircle size={16} className="text-green-500 mt-0.5" />
                              <span className="text-sm font-medium">{item}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upsells */}
                  {analysisData.sales_strategy.upsells && analysisData.sales_strategy.upsells.length > 0 && (
                    <div>
                      <h4 className="font-mono uppercase text-xs text-muted-foreground mb-2">Upsell Opportunities</h4>
                      <div className="space-y-2">
                        {analysisData.sales_strategy.upsells.map((item, idx) => (
                          <div key={idx} className="p-3 border border-primary/30 bg-primary/10 rounded-sm">
                            <div className="flex items-start gap-2">
                              <Plus size={16} className="text-primary mt-0.5" />
                              <span className="text-sm font-medium">{item}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Win Strategy */}
                {analysisData.sales_strategy.win_strategy && (
                  <div className="mt-4 p-4 border border-blue-500/30 bg-blue-500/10 rounded-sm">
                    <p className="text-xs font-mono uppercase text-blue-600 mb-2">Recommended Win Strategy:</p>
                    <p className="text-sm font-medium">{analysisData.sales_strategy.win_strategy}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <Card className="border-border bg-gradient-to-r from-green-500/10 to-primary/10">
            <CardContent className="p-4">
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    // Pass complete AI analysis to quote builder
                    const aiData = encodeURIComponent(JSON.stringify(analysisData));
                    navigate(`/quote-builder?estimate_id=${estimate.id}&ai_data=${aiData}`);
                  }}
                  className="w-full bg-gradient-to-r from-green-500 to-primary text-white hover:from-green-500/90 hover:to-primary/90 font-mono uppercase tracking-wider h-12"
                >
                  <FileSpreadsheet className="mr-2" size={18} />
                  Create Quote with AI Recommendations
                </Button>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate(`/quote-builder?estimate_id=${estimate.id}`)}
                    variant="outline"
                    className="flex-1 font-mono uppercase text-xs"
                  >
                    Create Quote (Blank)
                  </Button>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(analysisData, null, 2));
                      toast.success('Analysis copied!');
                    }}
                    variant="outline"
                    className="flex-1 font-mono uppercase text-xs"
                  >
                    Copy Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        );
      })()}
    </div>
  );
};

// Helper component for labels
const Label = ({ className, children }) => (
  <label className={className}>{children}</label>
);

export default EstimateManager;